<template>
	<div class="page container">
		<v2-back-btn />
		<div class="deal-pack" v-if="dealPack">
			<div class="deal-pack__header content">
				<div class="main">
					<div class="deal-pack__header--info">
						<div class="tag">
							{{ getDealsCount }} Deals
						</div>
						<div class="tag">
							<icon icon="v2-shield" colour="transparent" />
							{{ getLevel }}
						</div>
						<template v-if="dealPack.biddingSystems && dealPack.biddingSystems.length">
							<tooltip
								v-for="tag in dealPack.biddingSystems"
								:key="tag"
								text="Bidding system for this deal pack"
								position="top"
							>
								<div class="tag">
									{{ tag }}
								</div>
							</tooltip>
						</template>
						<tooltip
							v-else
							text="Bidding system for this deal pack"
							position="top"
						>
							<div class="tag">
								Any
							</div>
						</tooltip>
					</div>
					<div class="deal-pack__header--title">
						{{ dealPack.title }}
					</div>
					<div class="deal-pack__header--subtitle">
						{{ dealPack.description }}
					</div>

					<div class="deal-pack__header--description">
						<div
							data-element="introduction"
							v-if="dealPack.introduction"
						>
							<div
								data-element="introduction"
								v-html="dealPack.introduction"
							/>
						</div>
					</div>

					<div class="deal-pack__header--controls">
						<V2Button :disabled="dealPack.activated" @click="buyDealPack">
							{{ dealPack.activated ? 'Purchased' : 'Buy Deal Pack' }}
						</V2Button>
						<template v-if="!dealPack.activated">
							<span class="deal-pack__header--controls__price" :class="{discount: getDiscountString}"> {{ getCostString }} </span>
							<span v-if="getDiscountString" class="deal-pack__header--controls__price">{{ getDiscountString }}</span>
						</template>
						<v2-deal-pack-progress v-else :value="dealPack.activityProgress || 0" />
					</div>
				</div>
				<div class="side">
					<img class="deal-pack__header--img" :src="getDealPackImageUrl" />
				</div>
			</div>
			<div class="separator" />

			<div class="deal-pack__body content">
				<div class="main">
					<div class="deal-pack__body--title">
						Deals
					</div>
					<pack-deals-list :deal-pack="dealPack" />
				</div>
				<div class="side">
					<div class="deal-pack__body--info">
						<div class="deal-pack__body--info-title">
							<icon icon="v2-bookmark" colour="transparent" />
							Key Topics We’ll Cover
						</div>

						<div class="deal-pack__body--info-labels">
							<div class="tag" v-for="tag in dealPack.keywords" :key="tag">
								{{ tag }}
							</div>
						</div>

						<div class="deal-pack__body--info-title">
							<icon icon="v2-teacher" colour="transparent" />
							Your Instructor
						</div>

						<div class="deal-pack__body--info-teacher teacher" @click="openTeacher">
							<img class="teacher__avatar" :src="getImageSrc" />
							<div class="teacher__text">
								<div class="teacher__text--title">
									{{ dealPack.teacherName }}
								</div>
								<div class="teacher__text--subtitle">
									Teacher
								</div>
							</div>
						</div>

					</div>
				</div>
			</div>
		</div>

	</div>
</template>

<route>
	{
		"meta": {
			"showTopPicks": true
		}
	}
</route>

<script>

	import routeParams        from '@/mixins/routeParams';
	import api                from '@/services/api';
	import Icon               from '@/components/ui/Icon';
	import V2DealPackProgress from '@/components/dealPacks/V2DealPackProgress';
	import V2BackBtn          from '@/components/v2/ui/V2BackBtn';
	import V2Button           from '@/components/v2/ui/V2Button';
	import PackDealsList      from '@/components/dealPacks/PackDealsList';
	import Tooltip            from '@/components/ui/Tooltip';
	import getStripe          from '@/services/stripe';
	import MockImage from '@/assets/icons/v2/image-placeholder-small.svg';
	import { dealPackCost, dealPackDiscountCost } from '@/formatters';

	export default {
		metaInfo () {
			if (!this.dealPack) {
				return false;
			}
			return {
				title: this.dealPack.title
			};
		},

		layout: 'v2default',

		components: {
			PackDealsList,
			V2DealPackProgress,
			Icon,
			V2BackBtn,
			V2Button,
			Tooltip
		},

		mixins: [routeParams],

		data () {
			return {
				dealPack: null
			};
		},

		computed: {
			getDealPackTitle () {
				return this.dealPack?.title || '';
			},

			getLevel () {
				if (this.dealPack.minExperienceLevel === this.dealPack.maxExperienceLevel) {
				  return this.dealPack.minExperienceLevel;
				}

				return `${this.dealPack.minExperienceLevel}-${this.dealPack.maxExperienceLevel}`;
			},

			getDealsCount () {
				return this.dealPack.dealIds?.length || 0;
			},

			getBreadcrumbs () {
				return [
					{
						path: '/deal-store',
						text: 'Deal Store'
					},
					{
						path: this.getDealPackRoute,
						text: this.getDealPackTitle
					}
				].filter(Boolean);
			},

			getIsFreeByDiscount () {
				if (this.dealPack?.costWithDiscount) {
					return parseInt(this.dealPack?.costWithDiscount) === 0;
				}
				return false;
			},

			getIsFree () {
				return this.dealPack?.isFree === true || this.getIsFreeByDiscount;
			},

			getCostString () {
				if (this.getIsFree) {
					return 'free';
				}
				return dealPackCost(this.dealPack);
			},

			getDiscountString () {
				if (this.dealPack.isFree || this.getIsFreeByDiscount) {
					return false;
				}
				return dealPackDiscountCost(this.dealPack);
			},

			getDealPackImageUrl () {
				if (this.dealPack?.media?.[0]?.url) {
					return this.dealPack.media[0].url;
				}

				const focusTypes = {
					bidding: 'Bidding',
					'declarer play': 'Play',
					defence: 'Defence'
				};

				const focus = focusTypes[this.dealPack?.focus?.[0]] || 'Other';

				return `/card-images/${focus}_Lev${this.dealPack.maxExperienceLevel}.png`;
			},

			getImageSrc () {
				if (!this.dealPack?.teacherProfile?.media?.[0]?.url) {
					return MockImage;
				}
				return this.dealPack.teacherProfile.media[0].url;
			}
		},

		created () {
			this.setDealPack();
		},

		methods: {

			async buyDealPack () {
				const dealPack = await api.dealPacks.buyDealPackById({
					dealPackId: this.dealPack.id
				});
				if (!dealPack) {
					return false;
				}
				if (!dealPack.checkout) {
					// no fee required, no stripe checkout
					this.$router.push(`/my-deals/${this.dealPack.id}`);
					return;
				}
				const stripe = await getStripe();
				stripe.redirectToCheckout({
					sessionId: dealPack.checkout
				});
			},

			openTeacher () {
				this.$router.push(`/teachers/${this.dealPack.teacherProfileId}`);
			},

			async setDealPack () {
				this.$store.commit('ui/setLoadingIsVisible');

				const dealPack = await api.dealPacks.getPublishedDealPackById({
					dealPackId: this.getDealPackId
				});

				if (!dealPack) {
					this.$store.commit('ui/showError');
					return false;
				} else if (dealPack.activated) {
					await this.$router.push(`/my-deals/${dealPack.id}`);
					return false;
				}

				const teacherProfile = await api.teacherProfiles.getTeacherProfileById({
					profileId: dealPack.teacherProfileId
				});

        if (!teacherProfile) {
					this.$store.commit('ui/showError');
					return false;
				}

				dealPack.teacherProfile = teacherProfile;

				this.dealPack = dealPack;
				this.$store.commit('ui/setBreadcrumbs', {
					breadcrumbs: this.getBreadcrumbs
				});
				this.$store.commit('ui/setLoadingIsHidden');
			}
		}
	};

</script>

<style lang="scss" scoped>

.deal-pack {

  &__header {
    margin-top: 24px;
    background: $c-white-l;
    padding: 20px;
    border-radius: 4px;

		@media (max-width: 1279px) {
			display: flex;
			flex-direction: column-reverse;

			.main,
			.side {
				width: 100%;
			}

			.side {
				img {
					width: 100%;
				}
			}
		}

    .main {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .side {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--info {
      display: flex;
      gap: 6px;
      margin-bottom: 8px;

      .tag {
        background: $c-gray-l;
      }

      ::v-deep [data-element="tooltip-text-wrapper"] {
        min-height: rem(20);
        min-width: rem(116);
        margin-inline-start: -64px !important;

        [data-element="tooltip-text"] {
          font-size: 12px !important;
          font-weight: 400 !important;
          line-height: 14px !important;
        }
      }
    }

    &--title {
      @include font(28px, $c-darkest-navy, 500, 36px)
    }

    &--subtitle {
      @include font(18px, $c-darkest-navy, 400, 23px)
    }

    &--description {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      @include font(16px, $c-darkest-navy, 400, 20px);

      ::v-deep [data-element='introduction'] {
        font-size:rem(18);
        @include rich-text-content;
        @include rich-text-suits;
      }

      &-item {
        display: flex;
        align-items: center;

        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 13.5px;
          height: 13.5px;
          margin-right: 5.5px

        }

        .bold {
          font-weight: 700;
        }
      }

    }

    &--controls {
      display: flex;
      align-items: center;
      margin-top: 8px;
      gap: 16px;

      .btn {
        width: auto
      }

      &__price {
        @include font(20px, $c-dark-navy, 500, 26px);

        &.discount {
          text-decoration: line-through;
          opacity: 0.5;
        }
      }

      .progress-wrapper {
        width: 105px;
      }
    }

    &--img {
      width: 277px;
      height: 174px;
      object-fit: cover;
      border-radius: 4px;
    }
  }

  &__body {
		@media (max-width: 1279px) {
			display: flex;
			flex-direction: column;

			.main,
			.side {
				width: 100%;
			}
		}

    &--info {
      display: flex;
      flex-direction: column;

      &-title {
        display: flex;
        align-items: center;
        padding: 7.5px 0;
        margin-bottom: 12px;
        gap: 10px;
        @include font(18px, $c-darkest-navy, 700, 23px);
      }

      &-labels {
        margin-bottom: 36px;
        display: flex;
        flex-wrap: wrap;
        gap: 9px;

        .tag {
          background: $c-gray-l;
        }
      }

      &-teacher {
        display: flex;
        padding: 12px;
        border: 1px solid $c-gray-d2;
        gap: 12px;
        cursor: pointer;

        .teacher {
          &__avatar {
            width: 45px;
            height: 45px;
            object-fit: cover;
            border-radius: 2px;
          }

          &__text {
            display: flex;
            flex-direction: column;
            gap: 4px;

            &--title {
              @include font(18px, $c-dark-navy, 500, 23px);
            }

            &--subtitle {
              @include font(14px, $c-medium-navy, 700, 18px);

              ::v-deep [data-element='introduction'] {
                font-size: rem(18);
                @include rich-text-content;
                @include rich-text-suits;
              }
            }
          }
        }
      }
    }

    &--title {
      @include font(24px, $c-darkest-navy, 500, 31px);
      margin-bottom: 36px;
    }

    &--deals {
      margin-bottom: 36px;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 12px;
    }

    &--deals-full {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
    }
  }
}
</style>
